import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GOAL_TITLE, type Exercise, type Goal, type Template, type Workout } from '@jungles/shared';
import { muscleGroupTitle } from '../lib/workoutTitle';
import { api } from '../lib/api';
import { useApi } from '../lib/hooks';
import { useUser } from '../app/session';
import { useToast } from '../app/toast';
import { BackLink, ErrorState, Header, Loader, Screen, TabBar } from '../components/Chrome';

/** T5 · Готовые тренировки (SPEC §5). «Созданные для меня» — по цели из знакомства. */

type Filter = 'all' | 'forMe' | Goal | 'own';

const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all', label: 'Все' },
  { id: 'forMe', label: 'Созданные для меня' },
  { id: 'maintain', label: 'Поддержка' },
  { id: 'mass', label: 'Масса' },
  { id: 'cut', label: 'Жиросжигание' },
  { id: 'own', label: 'Мои' },
];

export function TemplatesScreen() {
  const user = useUser();
  const navigate = useNavigate();
  const toast = useToast();
  const { data, error, loading, reload } = useApi<Template[]>('/api/templates');
  const { data: catalog } = useApi<Exercise[]>('/api/exercises');
  const [filter, setFilter] = useState<Filter>('forMe');
  const [busy, setBusy] = useState<string | null>(null);

  const templates = useMemo(() => {
    const list = data ?? [];
    switch (filter) {
      case 'all':
        return list;
      case 'forMe':
        return list.filter((t) => t.goal === user.goal || t.isOwn);
      case 'own':
        return list.filter((t) => t.isOwn);
      default:
        return list.filter((t) => t.goal === filter);
    }
  }, [data, filter, user.goal]);

  const choose = async (template: Template) => {
    setBusy(template.id);
    try {
      const workout = await api.post<Workout>('/api/workouts', {
        type: 'ready',
        templateId: template.id,
      });
      navigate(`/workouts/${workout.id}`);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Не удалось начать тренировку');
      setBusy(null);
    }
  };

  return (
    <Screen tabs>
      <Header />
      <BackLink to="/workouts" label="Тренировки" />
      <h1>Готовые тренировки</h1>

      <div className="chips" role="group" aria-label="Фильтр программ">
        {FILTERS.map((item) => (
          <button
            key={item.id}
            className={filter === item.id ? 'chip on' : 'chip'}
            type="button"
            aria-pressed={filter === item.id}
            onClick={() => setFilter(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {loading ? <Loader /> : null}
      {error ? <ErrorState message={error} onRetry={reload} /> : null}

      {templates.map((template) => {
        const sets = template.items.reduce((total, item) => total + item.sets, 0);
        return (
          <section className="card" key={template.id}>
            <div className="row">
              <h3>{template.isOwn ? muscleGroupTitle(template.name, template.items, catalog) : template.name}</h3>
              {template.isOwn ? (
                <span className="tag">Моя</span>
              ) : template.goal ? (
                <span className="tag">{GOAL_TITLE[template.goal]}</span>
              ) : null}
            </div>
            <div className="meta">
              {template.items.length} упражнения · {sets} подходов · ~{template.approxMin} мин
            </div>
            <ul className="ex">
              {template.items.map((item, index) => (
                <li key={`${template.id}-${item.exerciseId}-${index}`}>
                  <span className="ei" aria-hidden="true">
                    <img src={item.icon} alt="" />
                  </span>
                  <span>{item.name}</span>
                  <span>
                    {item.sets} × {item.reps ?? 'макс'}
                  </span>
                </li>
              ))}
            </ul>
            <button
              className="b p"
              type="button"
              onClick={() => void choose(template)}
              disabled={busy === template.id}
            >
              {busy === template.id ? 'Готовим…' : 'Выбрать'}
            </button>
          </section>
        );
      })}

      {!loading && templates.length === 0 ? (
        <p className="empty">В этом фильтре программ пока нет</p>
      ) : null}

      <TabBar />
    </Screen>
  );
}
